import React, { useEffect, useRef } from 'react'
import { AdvancedButton } from '../AdvancedButton';
import { ButtonGroup, FormControl } from 'react-bootstrap';

interface ToolbarProps {
    onDelete?: () => void;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
    readOnly?: boolean;
    contentRef: React.RefObject<HTMLElement>;
    children?: React.ReactNode;
    showToolbar: boolean;
    setShowToolbar: (show: boolean) => void;
}

/**
 * Common toolbar component that can be reused across different block types
 */
export default function BlockToolbar(props: ToolbarProps) {
    const { onDelete, onMoveUp, onMoveDown, contentRef, children, showToolbar, setShowToolbar } = props;
    const toolbarRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (contentRef.current &&
                !contentRef.current.contains(event.target as Node) &&
                toolbarRef.current &&
                !toolbarRef.current.contains(event.target as Node)) {
                setShowToolbar(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [setShowToolbar, contentRef]);

    if (props.readOnly || !showToolbar) {
        return null;
    }

    const handleBlockDelete = () => {
        setShowToolbar(false);
        props.onDelete && props.onDelete();
    }

    return (
        <div
            ref={toolbarRef}
            className="editor-toolbar d-flex align-items-center p-1 mb-1 bg-white border rounded shadow-sm gap-2"
            style={{
                position: 'absolute',
                top: '-45px',
                left: '0',
                zIndex: 100,
            }}
        >
            {children}

            <div className="border-start mx-1" style={{ height: '20px' }}></div>
            <div className="d-flex gap-1">
                <AdvancedButton
                    size="sm"
                    icon="bi-arrow-up"
                    variant="outline-secondary"
                    onClick={onMoveUp}
                    disabled={!onMoveUp}
                />
                <AdvancedButton
                    size="sm"
                    icon="bi-arrow-down"
                    variant="outline-secondary"
                    onClick={onMoveDown}
                    disabled={!onMoveDown}
                />
            </div>

            {onDelete && (
                <>
                    <div className="border-start mx-1" style={{ height: '20px' }}></div>
                    <div>
                        <AdvancedButton
                            size="sm"
                            icon="bi-trash"
                            variant="danger"
                            onClick={handleBlockDelete}
                        />
                    </div>
                </>
            )}
        </div>
    );
}

interface TextAlignmentProps {
    value?: 'left' | 'center' | 'right';
    onChange: (align: 'left' | 'center' | 'right') => void;
}

export function TextAlignmentControls({ value, onChange }: TextAlignmentProps) {
    return (
        <div className="d-flex gap-1">
            <AdvancedButton
                size="sm"
                icon="bi-text-left"
                variant={value === 'left' ? 'primary' : 'outline-secondary'}
                onClick={() => onChange('left')}
            />
            <AdvancedButton
                size="sm"
                icon="bi-text-center"
                variant={value === 'center' ? 'primary' : 'outline-secondary'}
                onClick={() => onChange('center')}
            />
            <AdvancedButton
                size="sm"
                icon="bi-text-right"
                variant={value === 'right' ? 'primary' : 'outline-secondary'}
                onClick={() => onChange('right')}
            />
        </div>
    );
}

interface ColorPickerProps {
    value: string;
    onChange: (color: string) => void;
}

export function ColorPicker({ value, onChange }: ColorPickerProps) {
    return (
        <FormControl
            type="color"
            className="form-control form-control-sm p-0"
            value={value || '#000000'}
            onChange={(e) => onChange(e.target.value)}
            style={{ width: '38px', height: '30px' }}
        />
    );
}

interface FontSizePickerProps {
    value: number;
    onChange: (size: number) => void;
}

export function FontSizePicker({ value, onChange }: FontSizePickerProps) {
    return (
        <div>

            <select
                className="form-select form-select-sm"
                value={value || 16}
                onChange={(e) => onChange(Number(e.target.value))}
            >
                <option value={12}>12px</option>
                <option value={14}>14px</option>
                <option value={16}>16px</option>
                <option value={18}>18px</option>
                <option value={20}>20px</option>
                <option value={24}>24px</option>
            </select>
        </div>
    );
}

interface HeadingLevelPickerProps {
    value: 'h1' | 'h2' | 'h3' | 'h4';
    onChange: (level: 'h1' | 'h2' | 'h3' | 'h4') => void;
}

export function HeadingLevelPicker({ value, onChange }: HeadingLevelPickerProps) {
    return (
        <ButtonGroup size="sm">
            <AdvancedButton
                size="sm"
                variant={value === 'h1' ? 'primary' : 'outline-secondary'}
                onClick={() => onChange('h1')}
            >H1</AdvancedButton>
            <AdvancedButton
                size="sm"
                variant={value === 'h2' ? 'primary' : 'outline-secondary'}
                onClick={() => onChange('h2')}
            >H2</AdvancedButton>
            <AdvancedButton
                size="sm"
                variant={value === 'h3' ? 'primary' : 'outline-secondary'}
                onClick={() => onChange('h3')}
            >H3</AdvancedButton>
            <AdvancedButton
                size="sm"
                variant={value === 'h4' ? 'primary' : 'outline-secondary'}
                onClick={() => onChange('h4')}
            >H4</AdvancedButton>
        </ButtonGroup>
    );
}

interface ToggleButtonProps {
    icon: string;
    active: boolean;
    onChange: (value: boolean) => void;
}

export function ToggleButton({ icon, active, onChange }: ToggleButtonProps) {
    return (
        <AdvancedButton
            size="sm"
            icon={icon}
            variant={active ? 'primary' : 'outline-secondary'}
            onClick={() => onChange(!active)}
        />
    );
}

interface FontWeightToggleProps {
    value?: 'normal' | 'bold';
    onChange: (weight: 'normal' | 'bold') => void;
}

export function FontWeightToggle({ value, onChange }: FontWeightToggleProps) {
    const isBold = value === 'bold';
    return (
        <AdvancedButton
            size="sm"
            icon="bi-type-bold"
            variant={isBold ? 'primary' : 'outline-secondary'}
            onClick={() => onChange(isBold ? 'normal' : 'bold')}
        />
    );
}

interface SpacerSizePickerProps {
    value: 'small' | 'medium' | 'large';
    onChange: (size: 'small' | 'medium' | 'large') => void;
}

export function SpacerSizePicker({ value, onChange }: SpacerSizePickerProps) {
    return (
        <ButtonGroup size="sm">
            <AdvancedButton
                size="sm"
                variant={value === 'small' ? 'primary' : 'outline-secondary'}
                onClick={() => onChange('small')}
            >S</AdvancedButton>
            <AdvancedButton
                size="sm"
                variant={value === 'medium' ? 'primary' : 'outline-secondary'}
                onClick={() => onChange('medium')}
            >M</AdvancedButton>
            <AdvancedButton
                size="sm"
                variant={value === 'large' ? 'primary' : 'outline-secondary'}
                onClick={() => onChange('large')}
            >L</AdvancedButton>
        </ButtonGroup>
    );
}
